import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Badge,
  Button,
  Card,
  ConfirmDialog,
  Dialog,
  EmptyState,
  Input,
  Select,
  Table,
  Textarea,
  useToast,
  type BadgeTone,
  type Column,
} from "./ui";
import {
  deployService,
  type DeploymentItem,
  type DeployOverview,
  type DeployStatus,
  type DeployTarget,
  type DeployTargetInput,
} from "../services/deploy.service";

const TONE: Record<DeployStatus, BadgeTone> = {
  queued: "warn",
  running: "warn",
  succeeded: "ok",
  failed: "danger",
  rolled_back: "info",
};

const LABEL: Record<DeployStatus, string> = {
  queued: "Na fila",
  running: "Publicando",
  succeeded: "Publicado",
  failed: "Falhou",
  rolled_back: "Revertido",
};

interface FormState {
  uuid?: string;
  name: string;
  driver: "ssh" | "manual";
  environment: string;
  host: string;
  port: string;
  username: string;
  directory: string;
  domain: string;
  health_path: string;
  keep_releases: string;
  ssh_private_key: string;
  env_text: string;
}

const EMPTY: FormState = {
  name: "",
  driver: "ssh",
  environment: "production",
  host: "",
  port: "22",
  username: "",
  directory: "/opt/app",
  domain: "",
  health_path: "/api/health",
  keep_releases: "3",
  ssh_private_key: "",
  env_text: "",
};

/** Converte "CHAVE=valor" por linha em objeto; ignora linhas vazias/comentadas. */
function parseEnv(text: string): Record<string, string> {
  const result: Record<string, string> = {};
  for (const line of text.split("\n")) {
    const trimmed = line.trim();
    if (!trimmed || trimmed.startsWith("#")) continue;
    const index = trimmed.indexOf("=");
    if (index <= 0) continue;
    result[trimmed.slice(0, index).trim()] = trimmed.slice(index + 1).trim().replace(/^"|"$/g, "");
  }
  return result;
}

/**
 * Deploy em servidor próprio.
 * Chave SSH e variáveis do .env são enviadas uma vez e ficam criptografadas
 * no backend — nunca voltam para a tela.
 */
export default function DeployPanel({ projectId }: { projectId: string }) {
  const toast = useToast();
  const [data, setData] = useState<DeployOverview | null>(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState<FormState | null>(null);
  const [removing, setRemoving] = useState<DeployTarget | null>(null);
  const [detail, setDetail] = useState<DeploymentItem | null>(null);
  const timer = useRef<number | null>(null);

  const load = useCallback(async () => {
    try {
      setData(await deployService.overview(projectId));
    } catch (error) {
      toast.error((error as Error).message);
    }
  }, [projectId, toast]);

  useEffect(() => {
    void load();
  }, [load]);

  // Acompanha deploys em andamento sem recarregar a página.
  const running = useMemo(
    () => (data?.deployments ?? []).some((d) => d.status === "queued" || d.status === "running"),
    [data],
  );

  useEffect(() => {
    if (!running) return;
    timer.current = window.setTimeout(() => void load(), 4000);
    return () => {
      if (timer.current) window.clearTimeout(timer.current);
    };
  }, [running, load, data]);

  const save = useCallback(async () => {
    if (!form) return;
    const payload: DeployTargetInput = {
      name: form.name,
      driver: form.driver,
      environment: form.environment,
      host: form.host || null,
      port: Number(form.port) || 22,
      username: form.username || null,
      directory: form.directory || null,
      domain: form.domain || null,
      health_path: form.health_path || "/api/health",
      keep_releases: Number(form.keep_releases) || 3,
    };
    if (form.ssh_private_key.trim()) payload.ssh_private_key = form.ssh_private_key;
    if (form.env_text.trim()) payload.env_values = parseEnv(form.env_text);

    setBusy(true);
    try {
      if (form.uuid) await deployService.updateTarget(projectId, form.uuid, payload);
      else await deployService.createTarget(projectId, payload);
      toast.success("Servidor salvo.");
      setForm(null);
      await load();
    } catch (error) {
      toast.error((error as Error).message);
    } finally {
      setBusy(false);
    }
  }, [form, projectId, toast, load]);

  const run = useCallback(
    async (target: DeployTarget) => {
      setBusy(true);
      try {
        await deployService.run(projectId, target.uuid);
        toast.success("Publicação iniciada.");
        await load();
      } catch (error) {
        toast.error((error as Error).message);
      } finally {
        setBusy(false);
      }
    },
    [projectId, toast, load],
  );

  const rollback = useCallback(
    async (deployment: DeploymentItem) => {
      setBusy(true);
      try {
        await deployService.rollback(projectId, deployment.uuid);
        toast.success("Voltando para a versão anterior.");
        await load();
      } catch (error) {
        toast.error((error as Error).message);
      } finally {
        setBusy(false);
      }
    },
    [projectId, toast, load],
  );

  const openDetail = useCallback(
    async (deployment: DeploymentItem) => {
      try {
        setDetail(await deployService.show(projectId, deployment.uuid));
      } catch (error) {
        toast.error((error as Error).message);
      }
    },
    [projectId, toast],
  );

  const columns: Column<DeploymentItem>[] = [
    {
      key: "status",
      header: "Estado",
      render: (row) => <Badge tone={TONE[row.status]}>{LABEL[row.status]}</Badge>,
    },
    { key: "target", header: "Servidor", render: (row) => row.target_name ?? "—" },
    {
      key: "release",
      header: "Release",
      hideOnMobile: true,
      render: (row) => <span className="font-mono text-xs">{row.release ?? "—"}</span>,
    },
    {
      key: "when",
      header: "Quando",
      hideOnMobile: true,
      render: (row) => new Date(row.created_at).toLocaleString("pt-BR"),
    },
    {
      key: "actions",
      header: "",
      align: "right",
      render: (row) => (
        <div className="flex justify-end gap-2">
          <Button size="sm" variant="ghost" onClick={() => void openDetail(row)}>
            Ver etapas
          </Button>
          {row.can_rollback && row.status !== "rolled_back" && (
            <Button size="sm" variant="ghost" disabled={busy} onClick={() => void rollback(row)}>
              Reverter
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      {data && !data.enabled && (
        <Card>
          <p className="text-sm text-ink-muted">
            A publicação automática está desligada nesta instalação (DEPLOY_ENABLED). Você pode
            cadastrar servidores e continuar publicando manualmente.
          </p>
        </Card>
      )}

      <Card
        title="Servidores"
        action={
          <Button size="sm" onClick={() => setForm({ ...EMPTY })}>
            Novo servidor
          </Button>
        }
      >
        {data?.targets.length ? (
          <ul className="space-y-3">
            {data.targets.map((target) => (
              <li
                key={target.uuid}
                className="flex flex-col gap-2 rounded-lg border border-line/60 p-3 md:flex-row md:items-center md:justify-between"
              >
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-ink">{target.name}</span>
                    <Badge tone={target.is_active ? "ok" : "neutral"}>{target.environment}</Badge>
                    {target.driver === "manual" && <Badge tone="info">manual</Badge>}
                  </div>
                  <p className="font-mono text-xs text-ink-muted">
                    {target.username ?? "?"}@{target.host ?? "?"}:{target.port} · {target.directory ?? "—"}
                  </p>
                  <p className="text-xs text-ink-muted">
                    {target.has_private_key ? "Chave SSH cadastrada" : "Sem chave SSH"} ·{" "}
                    {target.env_keys.length} variáveis guardadas com segurança
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    disabled={busy || target.driver !== "ssh"}
                    onClick={() => void run(target)}
                  >
                    Publicar
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() =>
                      setForm({
                        ...EMPTY,
                        uuid: target.uuid,
                        name: target.name,
                        driver: target.driver,
                        environment: target.environment,
                        host: target.host ?? "",
                        port: String(target.port),
                        username: target.username ?? "",
                        directory: target.directory ?? "",
                        domain: target.domain ?? "",
                        health_path: target.health_path,
                        keep_releases: String(target.keep_releases),
                      })
                    }
                  >
                    Editar
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setRemoving(target)}>
                    Remover
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <EmptyState
            title="Nenhum servidor cadastrado"
            description="Cadastre um servidor com Docker e acesso por chave SSH para publicar por aqui."
          />
        )}
      </Card>

      <Card title="Histórico de publicações">
        <Table
          columns={columns}
          rows={data?.deployments ?? []}
          rowKey={(row) => row.uuid}
          empty={<EmptyState title="Ainda sem publicações" description="O histórico aparece aqui." />}
        />
      </Card>

      {data?.manual && (
        <Card title="Publicação manual (sempre disponível)">
          <p className="text-sm text-ink-muted">{data.manual.summary}</p>
          <ol className="mt-3 list-decimal space-y-1 pl-5 text-sm text-ink-muted">
            {data.manual.steps.map((step) => (
              <li key={step}>{step}</li>
            ))}
          </ol>
        </Card>
      )}

      <Dialog
        open={form !== null}
        onClose={() => setForm(null)}
        title={form?.uuid ? "Editar servidor" : "Novo servidor"}
        description="A chave privada e as variáveis ficam criptografadas no servidor do Builder e nunca voltam para a tela."
        size="lg"
        footer={
          <>
            <Button variant="ghost" onClick={() => setForm(null)}>
              Cancelar
            </Button>
            <Button disabled={busy || !form?.name} onClick={() => void save()}>
              Salvar
            </Button>
          </>
        }
      >
        {form && (
          <div className="grid gap-3 md:grid-cols-2">
            <Input
              label="Nome"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
            <Select
              label="Tipo"
              value={form.driver}
              onChange={(e) => setForm({ ...form, driver: e.target.value as "ssh" | "manual" })}
              options={[
                { value: "ssh", label: "Servidor remoto (SSH)" },
                { value: "manual", label: "Docker manual (só registro)" },
              ]}
            />
            <Input
              label="Endereço do servidor"
              value={form.host}
              onChange={(e) => setForm({ ...form, host: e.target.value })}
            />
            <Input
              label="Porta"
              value={form.port}
              onChange={(e) => setForm({ ...form, port: e.target.value })}
            />
            <Input
              label="Usuário"
              value={form.username}
              onChange={(e) => setForm({ ...form, username: e.target.value })}
            />
            <Input
              label="Diretório"
              value={form.directory}
              onChange={(e) => setForm({ ...form, directory: e.target.value })}
            />
            <Input
              label="Domínio (opcional)"
              value={form.domain}
              onChange={(e) => setForm({ ...form, domain: e.target.value })}
            />
            <Input
              label="Caminho de saúde"
              value={form.health_path}
              onChange={(e) => setForm({ ...form, health_path: e.target.value })}
            />
            <Input
              label="Versões mantidas para reverter"
              value={form.keep_releases}
              onChange={(e) => setForm({ ...form, keep_releases: e.target.value })}
            />
            <div className="md:col-span-2">
              <Textarea
                label="Chave SSH privada"
                rows={5}
                placeholder={form.uuid ? "Deixe em branco para manter a chave atual" : "-----BEGIN OPENSSH PRIVATE KEY-----"}
                value={form.ssh_private_key}
                onChange={(e) => setForm({ ...form, ssh_private_key: e.target.value })}
              />
            </div>
            <div className="md:col-span-2">
              <Textarea
                label="Variáveis do servidor (uma por linha: CHAVE=valor)"
                rows={5}
                placeholder={"APP_ENV=production\nDB_PASSWORD=..."}
                value={form.env_text}
                onChange={(e) => setForm({ ...form, env_text: e.target.value })}
              />
            </div>
          </div>
        )}
      </Dialog>

      <Dialog
        open={detail !== null}
        onClose={() => setDetail(null)}
        title="Etapas da publicação"
        description={detail ? `Release ${detail.release ?? "—"}` : undefined}
        size="lg"
      >
        <div className="max-h-[60vh] space-y-2 overflow-y-auto">
          {detail?.error && <p className="text-sm text-danger">{detail.error}</p>}
          {(detail?.logs ?? []).map((log, index) => (
            <div key={index} className="rounded-md border border-line/60 p-2">
              <div className="flex items-center justify-between">
                <span className="font-mono text-[11px] uppercase tracking-wide text-ink-muted">
                  {log.stage}
                </span>
                <Badge tone={log.level === "error" ? "danger" : "neutral"}>{log.level}</Badge>
              </div>
              <pre className="mt-1 whitespace-pre-wrap break-words text-xs text-ink-muted">
                {log.message}
              </pre>
            </div>
          ))}
          {detail && (detail.logs ?? []).length === 0 && (
            <EmptyState title="Sem etapas registradas" description="Esta publicação ainda não começou." />
          )}
        </div>
      </Dialog>

      <ConfirmDialog
        open={removing !== null}
        onCancel={() => setRemoving(null)}
        title="Remover servidor"
        description={`Remover "${removing?.name ?? ""}"? As publicações já feitas continuam no histórico.`}
        confirmLabel="Remover"
        destructive
        onConfirm={async () => {
          if (!removing) return;
          try {
            await deployService.deleteTarget(projectId, removing.uuid);
            setRemoving(null);
            await load();
          } catch (error) {
            toast.error((error as Error).message);
          }
        }}
      />
    </div>
  );
}
